(() => {
  // app/scripts/components/ProductImporterInvalid.js
  var ProductImporterInvalid = class extends HTMLElement {
    constructor() {
      super();
      this.invalidProducts = [];
      this.container = this.querySelector(".js-invalid-products");
      this.template = this.querySelector(".js-invalid-products tr").outerHTML;
      this.container.innerHTML = "";
      this.uploadButton = this.querySelector(".js-upload-button");
      this.cancelButton = this.querySelector(".js-cancel-button");
      this.resolve = null;
    }
    connectedCallback() {
      this.uploadButton.addEventListener("click", () => {
        this.classList.add("is-loading");
        this.resolve?.(true);
      });
      this.cancelButton.addEventListener("click", () => {
        this.resolve?.(false);
        this.clearInvalidProducts();
      });
    }
    addInvalidProducts(products) {
      this.classList.remove("is-loading");
      this.container.innerHTML = "";
      this.invalidProducts = products;
      products.forEach((product) => {
        this.container.innerHTML += this.template.replace("[row]", product.row).replace("[item_number]", product.item_number).replace("[quantity]", product.quantity).replace("[reason]", product.reason);
      });
    }
    async confirmUploadValidProducts() {
      if (this.invalidProducts.length > 0) {
        return new Promise((resolve) => {
          this.resolve = resolve;
        });
      }
      return true;
    }
    clearInvalidProducts() {
      this.container.innerHTML = "";
    }
  };
  customElements.define("products-importer-invalid", ProductImporterInvalid);

  // app/scripts/product-importer.js
  var {
    noDataFound: noDataFoundMessage,
    importFailedAllErrors: importFailedAllErrorsMessage,
    partialAlreadyExists: partialAlreadyExistsMessage,
    productNotFound: productNotFoundMessage,
    acceptedFileTypes: acceptedFileTypesMessage,
    moreThan500Records: moreThan500RecordsMessage
  } = theme.string.productImporter;
  var BATCH_SIZE = 10;
  var ProductImporter = class extends HTMLElement {
    constructor() {
      super();
      const $ = (selector) => this.querySelector(selector);
      this.callbacks = [];
      this.dataAlreadyExists = [];
      this.fileNameInput = $("input[name='file-name']");
      this.fileInput = $("input[name='file']");
      this.firstRowIsColumnHeadings = $("#first-row-is-column-headings");
      this.errorMessage = $(".js-error");
      this.warningMessage = $(".js-warning");
      this.invalidProductsContainer = $("products-importer-invalid");
    }
    connectedCallback() {
      this.fileInput.addEventListener("change", this.handleUploadFile.bind(this));
    }
    handleUploadFile(event) {
      this.setState("loading");
      const file = event.target.files[0];
      if (!file)
        return;
      this.fileNameInput.value = file.name;
      const reader = new FileReader();
      switch (true) {
        case file.name.endsWith(".csv"):
          reader.onload = this.handleReadCsvFile.bind(this);
          reader.readAsText(file);
          break;
        case file.name.endsWith(".xls"):
          reader.onload = this.handleReadXlsxFile.bind(this);
          reader.readAsArrayBuffer(file);
          break;
        case file.name.endsWith(".xlsx"):
          reader.onload = this.handleReadXlsxFile.bind(this);
          reader.readAsArrayBuffer(file);
          break;
        default:
          this.setState("error", acceptedFileTypesMessage);
          return;
      }
    }
    handleReadCsvFile(evt) {
      try {
        const workbook = XLSX.read(evt.target.result, { type: "string", raw: true });
        const data = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[0]], { header: 1, defval: "" });
        this.handleImportProducts(data);
      } catch (error) {
        this.setState("error", error.message);
        throw error;
      }
    }
    async handleReadXlsxFile(evt) {
      try {
        const buffer = evt.target.result;
        const workbook = XLSX.read(buffer, { type: "buffer" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });
        this.handleImportProducts(data);
      } catch (error) {
        this.setState("error", error.message);
        throw error;
      }
    }
    async handleImportProducts(data) {
      try {
        const filteredData = data.map(([manufacturer, ...rest]) => [String(manufacturer).trim(), ...rest]).filter(([manufacturer, quantity]) => quantity > 0);
        if (filteredData.length === 0) {
          throw new Error(noDataFoundMessage);
        }
        const variants = (await this.fetchVariants(filteredData)).map((variant) => {
          const foundData = filteredData.find(
            ([manufacturer]) => manufacturer.toUpperCase() === variant.additionalFields.productuniqueproductcode?.toUpperCase()
          );
          if (!foundData) {
            return null;
          }
          return {
            ...variant,
            quantity: +foundData[1] || 1
          };
        }).filter(Boolean);
        if (variants.length === 0) {
          throw new Error(importFailedAllErrorsMessage);
        }
        const alreadyExistsCount = variants.filter(
          (variant) => this.dataAlreadyExists.some((data2) => data2.ec_product_id === variant.ec_product_id)
        ).length;
        const invalidProducts = filteredData.map(([manufacturer, quantity], index) => {
          const data2 = {
            row: index + 1,
            item_number: manufacturer,
            quantity,
            reason: productNotFoundMessage
          };
          if (quantity > 0 && !variants.some((variant) => variant.additionalFields.productuniqueproductcode?.toUpperCase() === manufacturer.toUpperCase())) {
            return data2;
          }
          if (Number.isNaN(quantity)) {
            data2.reason = quantityErrorMessage;
            return data2;
          }
          return false;
        }).filter(Boolean);
        if (invalidProducts.length > 0) {
          this.invalidProductsContainer.addInvalidProducts(invalidProducts);
          this.setState("invalid");
        }
        if (!await this.invalidProductsContainer.confirmUploadValidProducts()) {
          return;
        }
        await this.dispatchUploadEvent(variants);
        switch (true) {
          case alreadyExistsCount > 0:
            this.setState("warning", partialAlreadyExistsMessage);
            break;
          default:
            this.setState("success");
        }
      } catch (error) {
        this.setState("error", error.message);
        throw error;
      } finally {
        this.reset();
      }
    }
    async fetchVariants(data) {
      try {
        const products = [];
        const newData = [...data];
        do {
          const query = newData.splice(0, BATCH_SIZE).map(([manufacturer]) => `@productuniqueproductcode="${manufacturer.toUpperCase()}"`).filter(Boolean).join(" OR ");
          const results = await CoveoSearchVariants.executeQuery(query);
          products.push(...results);
        } while (newData.length > 0);
        return products;
      } catch (error) {
        throw error;
      }
    }
    dispatchUploadEvent(data) {
      return Promise.all(this.callbacks.map((callback) => callback(data)));
    }
    onUpload(callback) {
      this.callbacks.push(callback);
    }
    setState(state, message) {
      switch (state) {
        case "loading":
          this.classList.remove("is-success", "is-error", "is-invalid", "is-warning");
          this.classList.add("is-loading");
          break;
        case "success":
          this.classList.add("is-success");
          this.classList.remove("is-invalid");
          break;
        case "error":
          this.classList.add("is-error");
          this.errorMessage.textContent = message;
          break;
        case "invalid":
          this.classList.add("is-invalid");
          break;
        case "warning":
          this.classList.add("is-warning");
          this.classList.remove("is-invalid");
          break;
        case "reset":
          this.classList.remove("is-loading", "is-invalid");
          break;
      }
    }
    setTimeoutToCloseMessage() {
      setTimeout(() => {
        this.classList.remove("is-success", "is-error", "is-invalid");
      }, 3e3);
    }
    async confirmUploadValidProducts(invalidProducts) {
      if (invalidProducts.length > 0) {
        this.invalidProductsContainer.clearInvalidProducts();
        this.invalidProductsContainer.addInvalidProducts(invalidProducts);
        this.setState("invalid");
        return new Promise((resolve, reject) => {
          this.uploadValidProductsResolve = resolve;
          this.uploadValidProductsReject = reject;
        });
      }
      return true;
    }
    reset() {
      this.setState("reset");
      this.fileNameInput.value = "";
      this.fileInput.value = "";
    }
    setAlreadyExists(dataAlreadyExists) {
      this.dataAlreadyExists = dataAlreadyExists;
    }
  };
  customElements.define("product-importer", ProductImporter);
})();
